import { Combobox } from '@base-ui/react/combobox'
import styled, { css, keyframes, type RuleSet } from 'styled-components'
import { ChevronDown } from 'sutra/components/Icons'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

// Visual vocabulary shared by `Select` and `MultiSelect`. Base UI ships a
// separate primitive per behaviour (Select vs Combobox), but the two controls
// must look identical, so the styles live here once and each component binds
// them to the parts it uses.

// Trigger size, using Input's vocabulary so the two line up side by side:
// 'sm' (30px, Select's default) or 'md' (40px, Input's md).
export type SelectSize = 'sm' | 'md'

const triggerSizeStyles = {
  sm: css`
    height: 30px;
    padding: 0 10px;
    font-size: 13px;
  `,
  md: css`
    height: 40px;
    padding: 0 14px;
    font-size: 14px;
  `,
}

export const triggerStyles = css<{ $size: SelectSize }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  ${({ $size }) => triggerSizeStyles[$size]}
  font-family: 'Poppins', system-ui, sans-serif;
  color: ${brand.stillNight[800]};
  background: ${brand.white};
  border: 1px solid ${gray[300]};
  border-radius: ${radii.sm}px;
  cursor: pointer;
  user-select: none;
  transition:
    background-color ${motion.duration.base}ms ${motion.ease},
    border-color ${motion.duration.base}ms ${motion.ease};

  &[data-popup-open] {
    border-color: ${brand.stillNight[800]};
  }
  &:hover:not(:disabled) {
    background: ${gray[100]};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }

  &[data-variant='borderless'] {
    background: transparent;
    border-color: transparent;
  }
  &[data-variant='borderless']:hover:not(:disabled) {
    background: ${gray[100]};
  }
  &[data-status='error'],
  &[data-status='error']:focus-within,
  &[data-status='error'][data-popup-open] {
    border-color: ${accents.crimsonBloom[500]};
  }
  &[data-status='warning'],
  &[data-status='warning']:focus-within,
  &[data-status='warning'][data-popup-open] {
    border-color: ${accents.goldenLight[600]};
  }
`

export const positionerStyles = css<{ $zIndex: number }>`
  z-index: ${({ $zIndex }) => $zIndex};
`

// Enter/exit are driven by Base UI's `data-starting-style` /
// `data-ending-style`, which it holds on the popup until the exit transition
// has finished.
export const popupStyles = css`
  box-sizing: border-box;
  width: max-content;
  min-width: var(--anchor-width);
  max-width: 360px;
  padding: 4px;
  background: ${brand.white};
  border: 1px solid ${gray[200]};
  border-radius: ${radii.sm}px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  outline: none;
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity ${motion.duration.fast}ms ${motion.ease},
    transform ${motion.duration.fast}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: translateY(-4px);
  }
`

// `$popupWidth`: true (default) makes the panel at least the trigger's width,
// false sizes it to its content, and a number is a fixed width in px.
export interface PopupWidth {
  $popupWidth?: boolean | number
}

const popupWidthStyles = ({
  $popupWidth = true,
}: PopupWidth): RuleSet<object> => {
  if (typeof $popupWidth === 'number') {
    return css`
      width: ${$popupWidth}px;
      min-width: 0;
      max-width: none;
    `
  }
  return css`
    min-width: ${$popupWidth ? 'var(--anchor-width)' : '0'};
  `
}

// The options panel of Select and MultiSelect: `popupStyles` plus the caller's
// width and a 360px height cap (the list scrolls, a search row stays put).
export const selectPopupStyles = css<PopupWidth>`
  ${popupStyles}
  ${popupWidthStyles}
  display: flex;
  flex-direction: column;
  max-height: min(360px, var(--available-height, 360px));
`

export const listStyles = css`
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  outline: none;
`

export const optionStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  border-radius: ${radii.xs}px;
  font-size: 13px;
  font-weight: 400;
  user-select: none;
  color: ${brand.stillNight[800]};
  background: transparent;
  cursor: pointer;
  outline: none;

  &[data-highlighted],
  &[data-selected] {
    background: ${accents.reflectionBlue[50]};
  }
  &[data-selected] {
    font-weight: 600;
  }
  &[data-disabled] {
    color: ${gray[400]};
    background: transparent;
    cursor: not-allowed;
  }

  &:hover > .option-accessory,
  &:focus-within > .option-accessory {
    visibility: visible;
  }
`

export const TriggerLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;

  &[data-placeholder] {
    color: ${gray[500]};
  }
`

export const Chevron = styled(ChevronDown)`
  flex-shrink: 0;
  color: ${gray[500]};
`

export const ComboboxPositioner = styled(Combobox.Positioner)<{
  $zIndex: number
}>`
  ${positionerStyles}
`

// `data-sutra-select-panel` marks the portaled panel so a caller's own
// outside-press check can tell a press inside it from one outside.
export const ComboboxPopup = styled(Combobox.Popup).attrs<{
  'data-sutra-select-panel'?: string
}>({
  'data-sutra-select-panel': '',
})<PopupWidth>`
  ${selectPopupStyles}
`

export const ComboboxList = styled(Combobox.List)`
  ${listStyles}
`

export const ComboboxItem = styled(Combobox.Item)`
  ${optionStyles}
`

export const SearchInput = styled(Combobox.Input)`
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 28px;
  margin-bottom: 4px;
  padding: 0 8px;
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 13px;
  color: ${brand.stillNight[800]};
  background: ${brand.white};
  border: 1px solid ${gray[200]};
  border-radius: ${radii.xs}px;
  outline: none;

  &:focus {
    border-color: ${accents.reflectionBlue[500]};
  }
`

const noResultsStyles = css`
  padding: 6px 10px;
  font-size: 13px;
  color: ${gray[400]};

  &:empty {
    display: none;
  }
`

export const NoResults = styled(Combobox.Empty)`
  ${noResultsStyles}
`

// The empty row of a plain (non-searchable) Select, which has no Combobox.Empty.
export const EmptyNote = styled.div`
  ${noResultsStyles}
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const Spinner = styled.span`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border: 2px solid ${gray[300]};
  border-top-color: ${accents.reflectionBlue[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

export const OptionCheck = styled(Combobox.ItemIndicator)`
  display: flex;
  flex-shrink: 0;
  color: ${accents.reflectionBlue[600]};
`

export const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
`

export const OptionAccessory = styled.span`
  display: flex;
  flex-shrink: 0;
  visibility: hidden;
`
